<section class="documents-page">
  <!-- Top Bar: Page Title + Description on left, New Document button on right, horizontal divider bottom -->
  <header class="page-topbar">
    <div class="topbar-title-block">
      <h1 class="page-title">{{ pageTitle() }}</h1>
      <p class="page-subtitle">{{ pageDescription() }}</p>
    </div>

    @if (!isTrashMode()) {
      <a class="btn-purple" routerLink="/documents/new">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="12" y1="18" x2="12" y2="12"></line>
          <line x1="9" y1="15" x2="15" y2="15"></line>
        </svg>
        <span>New Document</span>
      </a>
    }
  </header>

  <div class="documents-body">
    <!-- Search & Stats Toolbar -->
    <div class="search-bar-row">
    <div class="search-field-wrap">
      <input
        type="text"
        [(ngModel)]="searchQuery"
        [placeholder]="searchPlaceholder()"
        (keyup.enter)="applySearch()"
      />

      @if (searchQuery) {
        <button
          type="button"
          class="search-clear-btn"
          (click)="clearSearch()"
          [disabled]="loading"
          aria-label="Clear search"
          title="Clear search"
        >
          ×
        </button>
      }

      <button
        type="button"
        class="search-icon-btn"
        (click)="applySearch()"
        [disabled]="loading"
        aria-label="Search"
        title="Search"
      >
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="9" r="5.5"></circle>
          <line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
        </svg>
      </button>
    </div>

    <div class="total-documents-count">
      <strong>{{ total | number }}</strong>&nbsp;<span>{{ totalLabel() }}</span>
    </div>
  </div>

  <!-- Status Filter Chips Row -->
  <div class="status-filters-row">
    @for (filter of visibleFilters(); track filter.key) {
      <button
        type="button"
        class="status-chip"
        [class.active]="activeStatus === filter.key"
        (click)="selectFilter(filter.key)"
      >
        <span class="status-chip-label">{{ filter.label }}</span>
        <span class="status-chip-count">{{ getFilterCount(filter.key) | number }}</span>
      </button>
    }
  </div>

  @if (errorMessage) {
    <div class="alert-banner error">
      <p>{{ errorMessage }}</p>
    </div>
  }

  @if (successMessage) {
    <div class="alert-banner success">
      <p>{{ successMessage }}</p>
    </div>
  }

  <!-- Documents Table Card -->
  <div class="c-table-card">
    <table class="c-table">
      <thead>
        <tr>
          <th>TITLE</th>
          <th>STATUS</th>
          <th>RECIPIENTS</th>
          <th>LAST MODIFIED</th>
          <th class="actions-th">
            <span class="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        @if (loading) {
          <tr>
            <td colspan="5" class="c-table-loading-cell empty-row">
              <div class="c-loading-state">
                <div class="c-spinner"></div>
                <p class="c-loading-text">{{ loadingMessage() }}</p>
              </div>
            </td>
          </tr>
        } @else if (documents.length === 0) {
          <tr>
            <td colspan="5" class="empty-row">{{ emptyMessage() }}</td>
          </tr>
        } @else {
          @for (document of documents; track document._id) {
            <tr
              class="document-row"
              [class.read-only-row]="!canInteractWithRows()"
              [class.actions-visible]="showActions(document._id)"
              [class.menu-open]="isMenuOpen(document._id)"
              [attr.data-row-id]="document._id"
              [attr.tabindex]="canInteractWithRows() ? 0 : null"
              [attr.role]="canInteractWithRows() ? 'link' : null"
              [attr.aria-label]="rowAriaLabel()"
              (mouseenter)="onRowMouseEnter(document._id)"
              (mouseleave)="onRowMouseLeave(document._id)"
              (focusin)="onRowFocusIn(document._id)"
              (focusout)="onRowFocusOut($event, document._id)"
              (click)="openOverview(document)"
              (keydown)="onRowKeydown($event, document)"
            >
              <td>
                <div class="c-cell-title">
                  <p class="title">{{ document.document.title }}</p>
                  <p class="sub">{{ document.sourceType === 'template' ? (document.sourceTemplateName || 'Template') : 'Uploaded file' }}</p>
                </div>
              </td>
              <td>
                <span class="c-status-pill status-{{ document.status }}">
                  <span class="c-status-dot"></span>
                  <span>{{ statusLabel(document.status) }}</span>
                </span>
                @if (document.status === 'scheduled' && document.scheduledFor) {
                  <p class="scheduled-at">{{ formatScheduledAt(document.scheduledFor) }}</p>
                }
              </td>
              <td>
                <span class="c-badge-circle">{{ document.recipients.length }}</span>
              </td>
              <td>
                <span class="modified-date">{{ formatUpdatedAt(document.updatedAt) }}</span>
              </td>
              <td class="actions-cell" [class.menu-open-cell]="isMenuOpen(document._id)">
                @if (hasActions(document)) {
                  <div class="row-actions" [class.always-visible]="isTrashMode()">
                    @if (isTrashMode()) {
                      @if (quickActionFor(document); as quickAction) {
                        <button
                          type="button"
                          class="row-action-btn quick-action-btn"
                          [class.is-destructive]="quickAction.destructive"
                          [disabled]="!quickAction.available || isActionBusy(document._id, quickAction.id)"
                          [attr.aria-label]="quickAction.label"
                          [title]="quickAction.label"
                          (click)="handleAction($event, document, quickAction)"
                        >
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M6 6V3.5L3.5 6 6 8.5V6h5a4 4 0 1 1-3.2 6.4" />
                          </svg>
                        </button>
                      }
                    } @else {
                      <!-- Hover View Action Button -->
                      <button
                        type="button"
                        class="row-action-btn quick-view-btn"
                        [disabled]="isActionBusy(document._id, 'view')"
                        aria-label="View"
                        title="View"
                        (click)="$event.stopPropagation(); openView(document)"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>

                      <!-- Hover Edit Action Button -->
                      @if (canEdit(document)) {
                        <button
                          type="button"
                          class="row-action-btn quick-edit-btn"
                          [disabled]="isActionBusy(document._id, 'edit')"
                          aria-label="Edit"
                          title="Edit"
                          (click)="$event.stopPropagation(); openEdit(document)"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      }

                      <!-- Hover Download Quick Action -->
                      @if (downloadActionFor(document); as downloadAction) {
                        <button
                          type="button"
                          class="row-action-btn quick-download-btn"
                          [disabled]="!downloadAction.available || isActionBusy(document._id, downloadAction.id)"
                          [attr.aria-label]="downloadAction.label"
                          [title]="downloadAction.label"
                          (click)="handleAction($event, document, downloadAction)"
                        >
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M10 3v8m0 0 3-3m-3 3-3-3M4 14.5h12" />
                          </svg>
                        </button>
                      }
                    }

                    <!-- 3-dots Menu Shell -->
                    @if (actionsFor(document).length > 0) {
                      <div class="menu-shell" [class.is-open]="isMenuOpen(document._id)">
                        <button
                          type="button"
                          class="row-action-btn menu-toggle-btn"
                          [class.is-active]="isMenuOpen(document._id)"
                          aria-haspopup="menu"
                          [attr.aria-expanded]="isMenuOpen(document._id)"
                          aria-label="More actions"
                          title="More actions"
                          (click)="toggleMenu($event, document._id)"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.75" />
                            <circle cx="12" cy="12" r="1.75" />
                            <circle cx="12" cy="19" r="1.75" />
                          </svg>
                        </button>

                        @if (isMenuOpen(document._id)) {
                          <div class="actions-menu" role="menu" aria-label="Document actions">
                            @for (action of actionsFor(document); track action.id) {
                              <button
                                type="button"
                                class="menu-item"
                                role="menuitem"
                                [class.is-destructive]="action.destructive"
                                [disabled]="!action.available || isActionBusy(document._id, action.id)"
                                (click)="handleAction($event, document, action)"
                              >
                                {{ action.label }}
                              </button>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  <!-- Bottom Pagination matching Figma -->
  <footer class="pagination-footer">
    <div class="pagination-left">
      <span>Showing</span>
      <div class="per-page-select-wrap">
        <select [ngModel]="limit" (ngModelChange)="onLimitChange($event)" [disabled]="loading">
          @for (lim of availableLimits; track lim) {
            <option [value]="lim">{{ lim }}</option>
          }
        </select>
        <svg class="select-chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 8 10 12 14 8"></polyline>
        </svg>
      </div>
      <span>per page of <strong>{{ total | number }}</strong></span>
    </div>

    <div class="pagination-right">
      <div class="page-nav-controls">
        <button
          type="button"
          class="nav-btn"
          (click)="goToFirstPage()"
          [disabled]="loading || page <= 1"
          title="First page"
          aria-label="First page"
        >
          ««
        </button>
        <button
          type="button"
          class="nav-btn"
          (click)="goToPreviousPage()"
          [disabled]="loading || page <= 1"
          title="Previous page"
          aria-label="Previous page"
        >
          ‹
        </button>

        @for (p of pageNumbers(); track p) {
          <button
            type="button"
            class="page-num-btn"
            [class.active]="p === page"
            (click)="goToPage(p)"
            [disabled]="loading"
          >
            {{ p }}
          </button>
        }

        <button
          type="button"
          class="nav-btn"
          (click)="goToNextPage()"
          [disabled]="loading || page >= totalPages"
          title="Next page"
          aria-label="Next page"
        >
          ›
        </button>
        <button
          type="button"
          class="nav-btn"
          (click)="goToLastPage()"
          [disabled]="loading || page >= totalPages"
          title="Last page"
          aria-label="Last page"
        >
          »»
        </button>
      </div>

      <div class="goto-page-wrap">
        <span class="goto-label">Go To Page</span>
        <input
          type="number"
          min="1"
          [max]="totalPages"
          [(ngModel)]="goToPageInput"
          (keyup.enter)="jumpToPage()"
          [disabled]="loading"
          aria-label="Target page number"
        />
        <button
          type="button"
          class="goto-btn btn-outline"
          (click)="jumpToPage()"
          [disabled]="loading"
        >
          Go
        </button>
      </div>
    </div>
  </footer>
  </div>

  <!-- Delete Modal Dialog -->
  @if (deleteConfirmDocument; as documentToDelete) {
    <div class="delete-modal-mask" role="presentation" (click)="closeDeleteConfirm()">
      <section
        class="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-document-title"
        (click)="$event.stopPropagation()"
      >
        <div class="delete-modal-body">
          <h3 id="delete-document-title">{{ deleteModalTitle() }}</h3>
          <p>
            {{ deleteModalMessage() }}
            <strong>{{ documentToDelete.document.title }}</strong>.
          </p>
        </div>
        <footer class="delete-modal-actions">
          <button
            type="button"
            class="btn-outline"
            (click)="closeDeleteConfirm()"
            [disabled]="isActionBusy(documentToDelete._id, 'delete')"
          >
            Cancel
          </button>
          <button
            type="button"
            class="danger-btn"
            (click)="confirmDelete($event)"
            [disabled]="isActionBusy(documentToDelete._id, 'delete')"
          >
            {{ deleteModalConfirmLabel() }}
          </button>
        </footer>
      </section>
    </div>
  }
</section>
